import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { Button, Form, Row, Col, Modal, Table, Card } from "react-bootstrap";
import api, { BASE_URL } from "../../Utils/api";
import { IMaskInput } from "react-imask";
import { FaPencilAlt } from "react-icons/fa";
import { GlobalContext } from "../../Utils/GlobalContext";
import formatarData from "../../Utils/formatarData";
import DocumentosModal from "../Documentos/DocumentosModal";
import EmpresasModal from "../Empresas/EmpresasModal";
import { Salvar, Cancelar } from "../Generals/buttons";
import { Data } from "../Generals/Inputs";
import "./FuncionariosModal.css";


const FuncionariosModal = ({ show, handleClose, funcionario, fetchFuncionarios, defaultEmpresaId }) => {
  const { globalData } = useContext(GlobalContext);
  const entidadeID = globalData?.entidade_id || localStorage.getItem("entidade_id");
  const userId = globalData?.user_id || localStorage.getItem("user_id");

  const [formValues, setFormValues] = useState({
    nome: "",
    cpf: "",
    nascimento: "",
    data_contratacao: "",
    data_demissao: "",
    id_empresa: "",
    situacao: "A",
  });

  const [empresas, setEmpresas] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [selectedDocumento, setSelectedDocumento] = useState(null);
  const [showDocumentosModal, setShowDocumentosModal] = useState(false);
  const [showEscolhaEmpresaModal, setShowEscolhaEmpresaModal] = useState(false);
  const [showEmpresaModal, setShowEmpresaModal] = useState(false);
  const [classificacaoEmpresaCadastro, setClassificacaoEmpresaCadastro] = useState("F");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const idsEmpresasAntesCadastroRef = useRef(new Set());

  const recarregarEmpresas = useCallback(async (idEmpresaPreferencial = null, selecionarCriada = false) => {
    if (!entidadeID) return;

    try {
      const response = await api.get(
        `${BASE_URL}/api/empresas/ListarEmpresasParaFuncionarios/${entidadeID}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        }
      );
      const lista = Array.isArray(response.data?.data) ? response.data.data : [];
      setEmpresas(lista);

      const empresaCriada = selecionarCriada
        ? lista.find((empresa) => !idsEmpresasAntesCadastroRef.current.has(String(empresa.id)))
        : null;
      const idParaSelecionar = idEmpresaPreferencial ?? empresaCriada?.id;

      if (idParaSelecionar != null) {
        setFormValues((prev) => ({
          ...prev,
          id_empresa: String(idParaSelecionar),
        }));
      }
    } catch (error) {
      console.error("Erro ao buscar empresas:", error);
    }
  }, [entidadeID]);

  // Carrega fornecedores/filiais disponiveis para funcionario.
  useEffect(() => {
    if (show && entidadeID) {
      recarregarEmpresas();
    }
  }, [show, entidadeID, recarregarEmpresas]);

  // Carrega documentos do funcionário (apenas ao editar)
  const funcionarioId = funcionario?.id;
  const fetchDocumentosPorFuncionario = useCallback(async () => {
    if (!funcionarioId || !entidadeID || !userId) {
      setDocumentos([]);
      return;
    }
    try {
      const response = await api.get(
        `${BASE_URL}/api/documentos/ListarDocumentosPorFuncionario/${entidadeID}`,
        {
          params: { user_id: userId, idFuncionario: funcionarioId },
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        }
      );
      setDocumentos(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (err) {
      console.error("Erro ao buscar documentos do funcionário:", err);
      setDocumentos([]);
    }
  }, [funcionarioId, entidadeID, userId]);

  useEffect(() => {
    if (show && funcionarioId) {
      fetchDocumentosPorFuncionario();
    } else {
      setDocumentos([]);
    }
  }, [show, funcionarioId, fetchDocumentosPorFuncionario]);

  const handleEditDocumento = (doc) => {
    setSelectedDocumento(doc);
    setShowDocumentosModal(true);
  };

  const handleCloseDocumentosModal = () => {
    setShowDocumentosModal(false);
    setSelectedDocumento(null);
    fetchDocumentosPorFuncionario();
  };

  const abrirCadastroEmpresa = () => {
    idsEmpresasAntesCadastroRef.current = new Set(empresas.map((empresa) => String(empresa.id)));
    setShowEscolhaEmpresaModal(true);
  };

  const selecionarClassificacaoEmpresa = (classificacao) => {
    setClassificacaoEmpresaCadastro(classificacao);
    setShowEscolhaEmpresaModal(false);
    setShowEmpresaModal(true);
  };

  const handleCloseEmpresaModal = async (resultado) => {
    setShowEmpresaModal(false);
    const idCriado = resultado?.id;
    await recarregarEmpresas(idCriado != null ? String(idCriado) : null, Boolean(resultado));
  };

  // Carrega dados do funcionário quando editar
  useEffect(() => {
    if (show) {
      if (funcionario) {
        setFormValues({
          nome: funcionario.nome || "",
          cpf: funcionario.cpf || "",
          nascimento: funcionario.nascimento || "",
          data_contratacao: funcionario.data_contratacao || "",
          data_demissao: funcionario.data_demissao || "",
          id_empresa: funcionario.id_empresa || "",
          situacao: funcionario.situacao || "A",
        });
      } else {
        // Reset para novo funcionário (pré-seleciona empresa se fornecida)
        setFormValues({
          nome: "",
          cpf: "",
          nascimento: "",
          data_contratacao: "",
          data_demissao: "",
          id_empresa: defaultEmpresaId || "",
          situacao: "A",
        });
      }
      setError(null);
    }
  }, [show, funcionario, defaultEmpresaId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const verificarCamposObrigatorios = () => {
    const faltantes = [];
    if (!formValues.nome || formValues.nome.trim() === "") {
      faltantes.push("Nome");
    }
    if (!formValues.id_empresa) {
      faltantes.push("Empresa");
    }
    return faltantes;
  };

  const handleSave = async () => {
    setError(null);
    const camposFaltantes = verificarCamposObrigatorios();
    if (camposFaltantes.length > 0) {
      setError("Os seguintes campos obrigatórios não foram preenchidos: " + camposFaltantes.join(", ") + ".");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const headers = {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        "id_entidade": entidadeID,
      };

      if (funcionario) {
        await api.put(
          `${BASE_URL}/api/funcionarios/EditarFuncionario/${funcionario.id}`,
          formValues,
          { headers }
        );
      } else {
        await api.post(
          `${BASE_URL}/api/funcionarios/InserirFuncionario`,
          formValues,
          { headers }
        );
      }

      await fetchFuncionarios?.();
      handleClose();
    } catch (err) {
      console.error("Erro ao salvar funcionário:", err);
      setError(
        err.response?.data?.message ||
        "Erro ao salvar funcionário. Verifique os dados e tente novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        size="lg"
        dialogClassName="modal-dialog-top"
      >
        <Modal.Header closeButton style={{ background: "var(--surface-sunken)", borderColor: "var(--border-default)", paddingBottom: 12 }}>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 600, color: "var(--text-body)" }}>
            {funcionario ? "Editar Funcionário" : "Novo Funcionário"}
            {funcionario?.nome && (
              <span style={{ display: "block", fontSize: "0.82rem", fontWeight: 400, color: "var(--text-muted)", marginTop: 2 }}>
                {funcionario.nome}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "var(--surface-card)", color: "var(--text-body)" }}>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <Form>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Nome <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="nome"
                    value={formValues.nome}
                    onChange={handleInputChange}
                    placeholder="Nome completo do funcionário"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>CPF</Form.Label>
                  <IMaskInput
                    mask="000.000.000-00"
                    value={formValues.cpf}
                    onAccept={(value) =>
                      setFormValues((prev) => ({ ...prev, cpf: value }))
                    }
                    className="form-control"
                    placeholder="000.000.000-00"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Data de Nascimento</Form.Label>
                  <Data
                    name="nascimento"
                    value={formValues.nascimento}
                    onChange={(iso) =>
                      setFormValues((prev) => ({ ...prev, nascimento: iso }))
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Data de Contratação</Form.Label>
                  <Data
                    name="data_contratacao"
                    value={formValues.data_contratacao}
                    onChange={(iso) =>
                      setFormValues((prev) => ({ ...prev, data_contratacao: iso }))
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Data de Demissão</Form.Label>
                  <Data
                    name="data_demissao"
                    value={formValues.data_demissao}
                    onChange={(iso) =>
                      setFormValues((prev) => ({ ...prev, data_demissao: iso }))
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Fornecedor / Filial <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="fm-select-add-combo">
                    <Form.Select
                      name="id_empresa"
                      value={formValues.id_empresa}
                      onChange={handleInputChange}
                      className="fm-select-add-combo-select"
                    >
                      <option value="">Selecione o fornecedor ou filial</option>
                      {empresas.map((empresa) => (
                        <option key={empresa.id} value={empresa.id}>
                          {empresa.fantasia || empresa.razao_social}
                        </option>
                      ))}
                    </Form.Select>
                    <span className="fm-select-add-combo-button-wrapper">
                      <button
                        type="button"
                        className="fm-select-add-combo-button"
                        onClick={abrirCadastroEmpresa}
                        title="Adicionar fornecedor ou filial"
                        aria-label="Adicionar fornecedor ou filial"
                      >
                        <span className="fm-select-add-action-circle" aria-hidden="true" />
                      </button>
                    </span>
                  </div>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Situação</Form.Label>
                  <Form.Select
                    name="situacao"
                    value={formValues.situacao}
                    onChange={handleInputChange}
                  >
                    <option value="A">Ativo</option>
                    <option value="I">Inativo</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Form>

          <hr style={{ borderColor: "var(--border-default)" }} className="my-4" />
          <h6 style={{ fontWeight: 600, fontSize: "0.9rem", color: "var(--text-body)", marginBottom: 12 }}>Documentos</h6>
          <Card className="shadow-sm" style={{ border: "1px solid var(--border-default)" }}>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th style={{ fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "var(--tracking-caps)", color: "var(--table-head-text)", fontWeight: 700, borderTop: "none" }}>Tipo Documento</th>
                      <th style={{ fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "var(--tracking-caps)", color: "var(--table-head-text)", fontWeight: 700, borderTop: "none" }}>Vencimento</th>
                      <th style={{ fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "var(--tracking-caps)", color: "var(--table-head-text)", fontWeight: 700, borderTop: "none" }}>Situação</th>
                      <th style={{ width: "80px", fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "var(--tracking-caps)", color: "var(--table-head-text)", fontWeight: 700, borderTop: "none" }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentos.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-4 text-muted">
                          Nenhum registro encontrado
                        </td>
                      </tr>
                    ) : (
                      documentos.map((doc) => (
                        <tr key={doc.id}>
                          <td>{doc.tipo_documento || "—"}</td>
                          <td>{doc.dt_vencimento ? formatarData(doc.dt_vencimento) : "—"}</td>
                          <td>{doc.situacao === "A" ? "Ativo" : "Inativo"}</td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="p-1"
                              onClick={() => handleEditDocumento(doc)}
                              title="Editar"
                            >
                              <FaPencilAlt size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer className="modal-footer-padrao">
          <div className="modal-footer-acoes-esquerda" />
          <div className="modal-footer-acoes-direita">
            <Cancelar onClick={handleClose} disabled={loading} />
            <Salvar onClick={handleSave} loading={loading} />
          </div>
        </Modal.Footer>
      </Modal>

      {showDocumentosModal && (
        <DocumentosModal
          show={showDocumentosModal}
          handleClose={handleCloseDocumentosModal}
          documento={selectedDocumento}
          fetchDocumentos={fetchDocumentosPorFuncionario}
        />
      )}

      <Modal
        show={showEscolhaEmpresaModal}
        onHide={() => setShowEscolhaEmpresaModal(false)}
        centered
      >
        <Modal.Header closeButton className="fm-choice-modal-header">
          <Modal.Title>Adicionar Fornecedor/Filial</Modal.Title>
        </Modal.Header>
        <Modal.Body className="fm-choice-modal-body">
          <div className="fm-choice-actions">
            <Button variant="outline-primary" onClick={() => selecionarClassificacaoEmpresa("F")}>
              Fornecedor
            </Button>
            <Button variant="outline-primary" onClick={() => selecionarClassificacaoEmpresa("V")}>
              Filial
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {showEmpresaModal && (
        <EmpresasModal
          show={showEmpresaModal}
          handleClose={handleCloseEmpresaModal}
          empresa={null}
          fetchEmpresas={() => recarregarEmpresas()}
          classificacaoOverride={classificacaoEmpresaCadastro}
        />
      )}
    </>
  );
};

export default FuncionariosModal;

